/**
 * LeaderboardRow – single row in the group leaderboard table.
 * Extracted from GroupLeaderboardList for size compliance.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { MoreVertical } from 'lucide-react-native';
import { FriendAvatar } from '../shared/FriendAvatar';
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge';
import { PlayerLevelPill } from '../../../src/economy/components/PlayerLevelPill/PlayerLevelPill';
import type { PlayerRank } from '../../../src/economy/constants/playerRanks.constants';
import {
  GROUP_LEADERBOARD_AVATAR_SIZE,
  GROUP_LEADERBOARD_CREST_SIZE,
  getGroupLeaderboardStyles,
} from '../../../appStyles/_groupLeaderboard.styles';
import { GROUP_LEADERBOARD_YOU_LABEL } from '../../../src/friends/constants/groups.constants';
import { AVATAR_IDS } from '../../../constants/avatarIds';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import type { GroupLeaderboardEntry } from '../../../src/friends/helpers/groupLeaderboard.helper';

export interface LeaderboardRowProps {
  entry: GroupLeaderboardEntry;
  userId: string;
  myDisplayName: string;
  currentAvatarId: string | null;
  /** Current user's custom layered look; overrides the row's stored config for the self row. */
  currentAvatarConfig?: EquippedMap | null;
  /** Rank crest + level; null while unknown (specs/rank-on-social-screens AC-9/AC-14). */
  playerRank?: PlayerRank | null;
  styles: ReturnType<typeof getGroupLeaderboardStyles>;
  theme: { gray: Record<number, string> };
  menuTriggerRef: (ref: View | null) => void;
  onOpenMenu: (entry: GroupLeaderboardEntry) => void;
}

export const LeaderboardRow: React.FC<LeaderboardRowProps> = ({
  entry: e,
  userId,
  myDisplayName,
  currentAvatarId,
  currentAvatarConfig = null,
  playerRank = null,
  styles,
  theme,
  menuTriggerRef,
  onOpenMenu,
}) => {
  const isMe = e.userId === userId;
  const displayName = isMe ? myDisplayName : e.name;
  const statStyle = e.isPending ? styles.statPending : styles.stat;

  const delta = e.coinsDelta ?? 0;
  const coinsDisplay = delta > 0 ? `+${delta.toLocaleString()}` : delta.toLocaleString();
  const coinsStyle = e.isPending
    ? styles.statPending
    : delta > 0
      ? styles.coinsDeltaPos
      : delta < 0
        ? styles.coinsDeltaNeg
        : styles.stat;
  const rankStyle =
    e.rank === 1
      ? styles.rankFirst
      : e.rank === 2
        ? styles.rankSecond
        : e.rank === 3
          ? styles.rankThird
          : styles.rank;
  const isTopThree = e.rank === 1 || e.rank === 2 || e.rank === 3;
  const showYouHighlight = isMe && !e.isPending && !isTopThree;
  const rowStyle = [
    styles.row,
    e.rank === 1 && styles.rowFirst,
    e.rank === 2 && styles.rowSecond,
    e.rank === 3 && styles.rowThird,
    showYouHighlight && styles.rowYou,
    e.isPending && styles.rowPending,
  ];

  return (
    <View style={rowStyle}>
      {!isMe && (
        <View
          ref={menuTriggerRef}
          style={styles.rowMenuTopRight}
          collapsable={false}
        >
          <Pressable
            onPress={() => onOpenMenu(e)}
            style={styles.rowMenuButton}
            accessibilityLabel={`Options for ${e.name}`}
            accessibilityRole="button"
          >
            <MoreVertical size={22} color={theme.gray[600]} />
          </Pressable>
        </View>
      )}
      <Text style={rankStyle} numberOfLines={1}>#{e.rank}</Text>
      <View style={styles.avatar}>
        <View style={styles.avatarRankWrap}>
          <FriendAvatar
            size={GROUP_LEADERBOARD_AVATAR_SIZE}
            avatarId={
              ((isMe ? currentAvatarId : e.avatarId) ?? AVATAR_IDS.BLUE_WITH_HAIR) as DisplayAvatarId
            }
            avatarConfig={isMe ? currentAvatarConfig : e.avatarConfig}
            fallbackUrl={e.avatar?.startsWith('http') ? e.avatar : undefined}
            small={false}
            nameForInitials={displayName}
          />
          {playerRank != null && (
            <PlayerRankBadge
              rankIndex={playerRank.rank}
              size={GROUP_LEADERBOARD_CREST_SIZE}
              style={styles.rankCrest}
            />
          )}
        </View>
      </View>
      <View style={styles.nameBlock}>
        <Text style={styles.name} numberOfLines={2}>{displayName}</Text>
        {playerRank != null && (
          <PlayerLevelPill level={playerRank.level} style={styles.rankLevelPill} />
        )}
        {isMe && !e.isPending && (
          <View style={styles.youBadge}>
            <Text style={styles.youBadgeText}>{GROUP_LEADERBOARD_YOU_LABEL}</Text>
          </View>
        )}
        {e.isPending && (
          <View style={styles.pendingBadge}>
            <Text style={styles.pendingBadgeText}>Pending</Text>
          </View>
        )}
      </View>
      <View style={styles.statCellFirst}>
        <Text style={statStyle}>{e.challengesPlayed}</Text>
      </View>
      <View style={styles.statCell}>
        <Text style={statStyle}>{e.wins}</Text>
      </View>
      <View style={styles.statCellPts}>
        <Text style={coinsStyle}>{coinsDisplay}</Text>
      </View>
    </View>
  );
};
